import axiosInstance from '../config/axios.js';

const API_BASE = '/salaries';

const appendSalaryFormData = (formData, salaryData) => {
  const optionalFields = new Set(['payment_mode', 'transaction_id', 'remarks']);
  Object.keys(salaryData).forEach((key) => {
    const value = salaryData[key];
    if (value === null || value === undefined) return;
    if (optionalFields.has(key) && value === '') return;
    formData.append(key, value);
  });
};

const salaryService = {
  // Get base URL for direct downloads
  getBaseUrl: () => {
    return `${axiosInstance.defaults.baseURL}${API_BASE}`;
  },

  // Get all salaries with optional filters and pagination
  getAll: async (page = 1, perPage = 10, params = {}) => {
    const queryParams = {
      page,
      per_page: perPage,
      ...params
    };
    const response = await axiosInstance.get(API_BASE, { params: queryParams });
    return response.data;
  },

  // Get salary by ID
  getById: async (salaryId) => {
    const response = await axiosInstance.get(`${API_BASE}/${salaryId}`);
    return response.data;
  },

  // Get salaries by user ID
  getByUserId: async (userId) => {
    const response = await axiosInstance.get(`${API_BASE}/user/${userId}`);
    return response.data;
  },

  // Get available users for salary creation
  getAvailableUsers: async (month = null, year = null) => {
    const params = {};
    if (month) params.month = month;
    if (year) params.year = year;
    const response = await axiosInstance.get(`${API_BASE}/available-users`, { params });
    return response.data;
  },

  // Calculate salary based on attendance
  calculateFromAttendance: async (userId, month, year) => {
    const params = { user_id: userId, month, year };
    const response = await axiosInstance.get('/salaries/calculate-from-attendance', { params });
    return response.data;
  },

  // Calculate salary with loan deductions
  calculateWithDeductions: async (userId, month, year, salaryType = null) => {
    const params = { user_id: userId, month, year };
    if (salaryType) params.salary_type = salaryType;
    const response = await axiosInstance.get('/salaries/calculate-with-deductions', { params });
    return response.data;
  },

  // Create salary with deductions
  createWithDeductions: async (salaryData, paymentScreenshot = null) => {
    const formData = new FormData();
    appendSalaryFormData(formData, salaryData);

    // Append payment screenshot if provided
    if (paymentScreenshot) {
      formData.append('payment_screenshot', paymentScreenshot);
    }

    const response = await axiosInstance.post('/salaries/create-with-deductions', formData, {
      headers: {
        'Content-Type': undefined,
      },
    });
    return response.data;
  },

  // Get monthly report
  getMonthlyReport: async (month, year) => {
    const response = await axiosInstance.get(`${API_BASE}/report/monthly`, {
      params: { month, year }
    });
    return response.data;
  },

  // Get statistics
  getStatistics: async (month = null, year = null) => {
    const params = {};
    if (month) params.month = month;
    if (year) params.year = year;
    const response = await axiosInstance.get(`${API_BASE}/statistics`, { params });
    return response.data;
  },

  // Get available months
  getAvailableMonths: async () => {
    const response = await axiosInstance.get(`${API_BASE}/months`);
    return response.data;
  },

  // Create new salary
  create: async (salaryData, paymentScreenshot = null) => {
    const formData = new FormData();
    appendSalaryFormData(formData, salaryData);

    // Append payment screenshot if provided
    if (paymentScreenshot) {
      formData.append('payment_screenshot', paymentScreenshot);
    }

    const response = await axiosInstance.post(API_BASE, formData, {
      headers: {
        'Content-Type': undefined,
      },
    });
    return response.data;
  },

  // Update salary
  update: async (salaryId, salaryData, paymentScreenshot = null) => {
    const formData = new FormData();
    appendSalaryFormData(formData, salaryData);

    // Append payment screenshot if provided
    if (paymentScreenshot) {
      formData.append('payment_screenshot', paymentScreenshot);
    }

    const response = await axiosInstance.put(`${API_BASE}/${salaryId}`, formData, {
      headers: {
        'Content-Type': undefined,
      },
    });
    return response.data;
  },

  // Delete salary
  delete: async (salaryId) => {
    const response = await axiosInstance.delete(`${API_BASE}/${salaryId}`);
    return response.data;
  },

  // Mark salary as credited
  markAsCredited: async (salaryId, paymentData, paymentScreenshot = null) => {
    const formData = new FormData();
    appendSalaryFormData(formData, paymentData);

    // Append payment screenshot if provided
    if (paymentScreenshot) {
      formData.append('payment_screenshot', paymentScreenshot);
    }

    const response = await axiosInstance.post(`${API_BASE}/${salaryId}/credit`, formData, {
      headers: {
        'Content-Type': undefined,
      },
    });
    return response.data;
  },

  // Get payment screenshot URL
  getPaymentScreenshot: (salaryId) => {
    return `${axiosInstance.defaults.baseURL}${API_BASE}/${salaryId}/screenshot`;
  },

  // Download salary slip PDF (uses axios auth interceptor)
  downloadSalarySlipFile: async (userId, month, year) => {
    const response = await axiosInstance.get(`${API_BASE}/download/slip`, {
      params: { user_id: userId, month, year },
      responseType: 'blob',
    });
    return response.data;
  },

  // Download salary slip - legacy URL helper
  downloadSalarySlip: (userId, month, year) => {
    return `${axiosInstance.defaults.baseURL}${API_BASE}/download/slip?user_id=${userId}&month=${month}&year=${year}`;
  }
};

export default salaryService;